import {
  TextField,
  Button,
  FormControlLabel,
  Switch,
  FormLabel,
} from "@mui/material";
import FormFieldErrorClass from "./models/formFieldError";
import ErrorIcon from "@mui/icons-material/Error";
import { useState, useEffect } from "react";
import isEmail from "validator/lib/isEmail";
import {
  InternationalPhoneInput,
  isPhoneNumberValid,
} from "../PhoneNumberInput";
import {
  FormContainer,
  FormSubmitError,
  ActionButtonsContainer,
  HeaderNote,
  InputFieldsWrapper,
} from "./shared";
import TableSubHeader from "../Page/tableSubHeader";
import styled from "styled-components";
import colors from "../../theme/colors";
import { NoChartDataPlaceholder } from "../NoDataPlaceholder";
import { TrashIcon } from "../../theme/icons";

const HeaderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 12px 0;
`;

const NetworkSelectionContainer = styled.div`
  margin: 8px 0 20px 0;
  display: flex;
  gap: 20px;
`;

const NetworkOrganization = styled.div`
  margin: 20px 0;
`;

const NetworkOrganizationPermissions = styled.div``;

const NetworkOrganizationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
  h4 {
    margin: 0;
    padding: 0;
    font-size: 16px;
  }
`;

const PermissionSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NetworkOrganizationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NetworksErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 0 0;
`;

const FormLabelCustom = styled.div`
  color: ${colors.grayDark};
  &.enabled {
    color: ${colors.black};
  }
`;

const NetworkOrganizationLabel = styled.div`
  color: ${colors.grayDark};
`;

const AdminUserForm = ({
  handleCancel = () => {},
  handleSave = () => {},
  loading = false,
  error = "",
  user,
  isEdit = false,
  showDeactivate = false,
  handleDeactivateUser = () => {},
  handleReactivateUser = () => {},
  handleResendInvite = () => {},
  organizationsList = [],
  networksList = [],
}) => {
  const [save, setSave] = useState(false);

  //Use the User object if provided
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneCountryData, setPhoneCountryData] = useState({});
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("CUSTOMER");
  const [userOrganizationId, setUserOrganizationId] = useState(
    organizationsList[0]?.id || ""
  );
  const [userCustomerRole, setUserCustomerRole] = useState("TECH");
  const [userEmployeeRole, setUserEmployeeRole] = useState("ADMIN");
  const [availableNetworksList, setAvailableNetworksList] =
    useState(networksList);
  const [networkId, setNetworkId] = useState(networksList[0]?.id || "");
  const [network, setNetwork] = useState(networksList[0] || "");
  const [userNetworks, setUserNetworks] = useState([]);
  const [
    userNetworkOrganizationPermissionList,
    setUserNetworkOrganizationPermissionList,
  ] = useState([]);

  const [firstNameError, setFirstNameError] = useState(
    new FormFieldErrorClass()
  );
  const [lastNameError, setLastNameError] = useState(new FormFieldErrorClass());
  const [phoneError, setPhoneError] = useState(new FormFieldErrorClass());
  const [emailError, setEmailError] = useState(new FormFieldErrorClass());
  const [userTypeError, setUserTypeError] = useState(new FormFieldErrorClass());
  const [userOrganizationIdError, setUserOrganizationIdError] = useState(
    new FormFieldErrorClass()
  );
  const [userCustomerRoleError, setUserCustomerRoleError] = useState(
    new FormFieldErrorClass()
  );
  const [userEmployeeRoleError, setUserEmployeeRoleError] = useState(
    new FormFieldErrorClass()
  );
  const [networkIdError, setNetworkIdError] = useState(
    new FormFieldErrorClass()
  );
  const [userNetworksError, setUserNetworksError] = useState(
    new FormFieldErrorClass()
  );
  const userTypes = [
    { value: "CUSTOMER", name: "Organization" },
    { value: "EMPLOYEE", name: "Boreas Employee" },
    { value: "NETWORK", name: "Network" },
  ];
  const userTypeCustomerRoles = [
    { value: "DIRECTOR", name: "Director" },
    { value: "TECH", name: "Tech" },
    { value: "SUPERVISOR", name: "Supervisor" },
  ];
  const userTypeEmployeeRoles = [{ value: "ADMIN", name: "System Admin" }];

  useEffect(() => {
    if (user && isEdit) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setPhone(user.phone);
      setEmail(user.email);
      setUserType(user.type);
      if (user.type === "CUSTOMER") {
        setUserCustomerRole(user.role);
        setUserOrganizationId(user.organizationId);
      } else if (user.type === "EMPLOYEE") {
        setUserEmployeeRole(user.role);
      } else if (user.type === "NETWORK") {
        //hydrate user.networks.organizations and remove from available list
        const tempNetworkList = [...availableNetworksList];
        const _userNetworks = user.networks.map((_network) => {
          //remove network from the available list
          const indexToRemove = tempNetworkList.findIndex(
            (i) => i.id === _network.id
          );
          tempNetworkList.splice(indexToRemove, 1);

          //hydrate organizations
          _network.organizations = organizationsList.filter((o) =>
            _network.organizationIds.includes(o.id)
          );

          return _network;
        });

        setAvailableNetworksList(tempNetworkList);

        if (tempNetworkList.length) {
          setNetworkId(tempNetworkList[0].id);
          setNetwork(tempNetworkList[0]);
        } else {
          setNetworkId("");
          setNetwork({});
        }

        setUserNetworks(_userNetworks);

        const _l = user.networkOrganizationPermissionList
          .filter((l) => l.accessLevel === "FULL-ACCESS")
          ?.map((i) => {
            return {
              fullAccess: true,
              networkId: i.networkId,
              organizationId: i.organizationId,
            };
          });

        setUserNetworkOrganizationPermissionList(_l);
      }
    }
  }, [user, isEdit]);

  const formFieldErrors = [
    firstNameError,
    lastNameError,
    phoneError,
    emailError,
    userTypeError,
    userOrganizationIdError,
    userCustomerRoleError,
    userEmployeeRoleError,
    networkIdError,
    userNetworksError,
  ];

  const validate = {
    firstName: (value) => {
      setFirstNameError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setFirstNameError({
          hasError: true,
          message: "First Name Required",
        });
        return;
      }
      if (value.length > 100) {
        setFirstNameError({
          hasError: true,
          message: "First Name Max 100 Charaters",
        });
        return;
      }
    },
    lastName: (value) => {
      setLastNameError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setLastNameError({
          hasError: true,
          message: "Last Name Required",
        });
        return;
      }
      if (value.length > 100) {
        setLastNameError({
          hasError: true,
          message: "Last Name Max 100 Charaters",
        });
        return;
      }
    },
    phone: (value) => {
      setPhoneError({
        hasError: false,
        message: "",
      });

      if (!value) {
        setPhoneError({
          hasError: true,
          message: "Phone Required",
        });
        return;
      }
      if (!isPhoneNumberValid(value)) {
        setPhoneError({
          hasError: true,
          message: "Phone Number Not Valid",
        });
        return;
      }
    },
    email: (value) => {
      setEmailError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setEmailError({
          hasError: true,
          message: "Email Required",
        });
        return;
      }
      if (!isEmail(value)) {
        setEmailError({
          hasError: true,
          message: "Email Format Not Valid",
        });
        return;
      }
    },
    userType: (value) => {
      setUserTypeError({
        hasError: false,
        message: "",
      });
      if (!userTypes.find((x) => x.value === value)) {
        setUserTypeError({
          hasError: true,
          message: "User Type Not Supported",
        });
        return;
      }
      if (!value) {
        setUserTypeError({
          hasError: true,
          message: "User Type Required",
        });
        return;
      }
    },
    userOrganizationId: (value) => {
      setUserOrganizationIdError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setUserOrganizationIdError({
          hasError: true,
          message: "User Organization Required",
        });
        return;
      }
    },
    userCustomerRole: (value) => {
      setUserCustomerRoleError({
        hasError: false,
        message: "",
      });
      if (!userTypeCustomerRoles.find((x) => x.value === value)) {
        setUserCustomerRoleError({
          hasError: true,
          message: "User Role Not Supported",
        });
        return;
      }
      if (!value) {
        setUserCustomerRoleError({
          hasError: true,
          message: "User Role Required",
        });
        return;
      }
    },
    userEmployeeRole: (value) => {
      setUserEmployeeRoleError({
        hasError: false,
        message: "",
      });
      if (!userTypeEmployeeRoles.find((x) => x.value === value)) {
        setUserEmployeeRoleError({
          hasError: true,
          message: "User Role Not Supported",
        });
        return;
      }
      if (!value) {
        setUserEmployeeRoleError({
          hasError: true,
          message: "User Role Required",
        });
        return;
      }
    },
    networkId: (value) => {
      setNetworkIdError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setNetworkIdError({
          hasError: true,
          message: "Network Required",
        });
        return;
      }
    },
    userNetworks: (_userType, _userNetworks) => {
      //clear errors
      setUserNetworksError({
        hasError: false,
        message: "",
      });
      if (_userType === "NETWORK") {
        if (!_userNetworks.length) {
          setUserNetworksError({
            hasError: true,
            message:
              "Network Users are required to have minimum 1 network. Add network above.",
          });
        }
      } else {
        //clear errors
        setUserNetworksError({
          hasError: false,
          message: "",
        });
      }
    },
  };

  useEffect(() => {
    validate.userNetworks(userType, userNetworks);
  }, [userType, userNetworks]);

  const handlers = {
    firstNameChange: (e) => {
      const value = e.target.value;
      validate.firstName(value);
      setFirstName(e.target.value);
    },
    lastNameChange: (e) => {
      const value = e.target.value;
      validate.lastName(value);
      setLastName(e.target.value);
    },
    phoneChange: (phoneData) => {
      console.log("phoneData", phoneData);
      validate.phone(phoneData.phone);
      setPhone(phoneData.phone);
      setPhoneCountryData(phoneData.country);
    },
    emailChange: (e) => {
      const value = e.target.value;
      validate.email(value);
      setEmail(e.target.value);
    },
    userTypeChange: ({ target }) => {
      const { value } = target;
      validate.userType(value);
      setUserType(value);
    },
    userOrganizationIdChange: ({ target }) => {
      const { value } = target;
      validate.userOrganizationId(value);
      setUserOrganizationId(value);
    },
    userCustomerRoleChange: ({ target }) => {
      const { value } = target;
      validate.userCustomerRole(value);
      setUserCustomerRole(value);
    },
    userEmployeeRoleChange: ({ target }) => {
      const { value } = target;
      validate.userEmployeeRole(value);
      setUserEmployeeRole(value);
    },
    networkIdChange: ({ target }) => {
      const { value } = target;
      validate.networkId(value);
      setNetworkId(value);
      setNetwork({ ...networksList.find((n) => n.id === value) });
    },
    addUserToNetworkClick: () => {
      setUserNetworks([...userNetworks, network]);
      //remove network from the available list
      const tempNetworkList = [...availableNetworksList];
      const indexToRemove = tempNetworkList.findIndex(
        (i) => i.id === network.id
      );
      tempNetworkList.splice(indexToRemove, 1);

      setAvailableNetworksList(tempNetworkList);

      if (tempNetworkList.length) {
        setNetworkId(tempNetworkList[0].id);
        setNetwork(tempNetworkList[0]);
      } else {
        setNetworkId("");
        setNetwork({});
      }
    },
    removeUserFromNetworkClick: (networkIdToRemove) => {
      console.log(networkIdToRemove);
      //get network
      const _network = networksList.find((n) => n.id === networkIdToRemove);
      console.log("remove network", _network);

      //remove network from user networks
      const tempUserNetworks = [...userNetworks];
      const indexToRemove = tempUserNetworks.findIndex(
        (i) => i.id === _network.id
      );
      tempUserNetworks.splice(indexToRemove, 1);
      console.log("user networks", tempUserNetworks);
      setUserNetworks(tempUserNetworks);

      //remove organization permissions for removed network
      const _tempList = userNetworkOrganizationPermissionList.filter(
        (n) => n.networkId !== networkIdToRemove
      );
      console.log("userNetworkOrganizationPermissionList", _tempList);
      setUserNetworkOrganizationPermissionList(_tempList);

      //add network back to available networks
      console.log("available networks", [...availableNetworksList, _network]);
      setAvailableNetworksList([...availableNetworksList, _network]);
      if (!availableNetworksList.length) {
        setNetworkId(_network.id);
        setNetwork(_network);
      }
    },
    networkOrganizationPermissionChange: ({
      _networkId,
      _organizationId,
      _fullAccess,
    }) => {
      //this is an opt in full access, it's assumed read only
      console.log({ _networkId, _organizationId, _fullAccess });

      if (_fullAccess) {
        //add to list
        setUserNetworkOrganizationPermissionList([
          ...userNetworkOrganizationPermissionList,
          {
            networkId: _networkId,
            organizationId: _organizationId,
            fullAccess: _fullAccess,
          },
        ]);
      } else {
        //remove from list
        const _tempList = [...userNetworkOrganizationPermissionList];
        const _indexToRemove = _tempList.findIndex(
          (i) =>
            i.networkId === _networkId && i.organizationId === _organizationId
        );
        _tempList.splice(_indexToRemove, 1);
        setUserNetworkOrganizationPermissionList(_tempList);
      }
    },
    save: () => {
      //validate all
      validate.firstName(firstName);
      validate.lastName(lastName);
      validate.phone(phone);
      validate.email(email);
      validate.userType(userType);
      validate.userOrganizationId(userOrganizationId);
      validate.userCustomerRole(userCustomerRole);
      validate.userEmployeeRole(userEmployeeRole);
      validate.userNetworks(userType, userNetworks);
      setSave(true);
    },
    cancel: () => {
      handleCancel();
    },
    deactivateUser: () => {
      handleDeactivateUser(user);
    },
    reactivateUser: () => {
      handleReactivateUser(user);
    },
    resendUserInvite: () => {
      handleResendInvite(user);
    },
  };

  useEffect(() => {
    if (save) {
      setSave(false);
      if (formFieldErrors.filter((e) => e.hasError).length) {
        return;
      }
      const phoneCountryDialCode = phoneCountryData.dialCode;
      const phoneCountryCode = phoneCountryData.iso2;

      const userData = {
        firstName,
        lastName,
        phoneCountryDialCode,
        phoneCountryCode,
        phone,
        email,
        type: userType,
      };

      //roles and permissions formatting, dynamic per user type
      switch (userType) {
        case "CUSTOMER":
          userData.role = userCustomerRole;
          userData.organizationId = userOrganizationId;
          break;
        case "EMPLOYEE":
          userData.role = userEmployeeRole;
          break;
        case "NETWORK":
          userData.role = "NETWORK";
          userData.networkIds = userNetworks.map((n) => n.id);
          userData.networkOrganizationPermissionList =
            userNetworkOrganizationPermissionList;
          break;
        default: {
        }
      }

      console.log(userData);
      // send data
      handleSave(userData);
    }
  }, [save, formFieldErrors]);

  return (
    <FormContainer>
      <InputFieldsWrapper>
        <HeaderDetails>
          <b>User Details</b>
          <TableSubHeader>To create user, enter details below</TableSubHeader>
        </HeaderDetails>
        <TextField
          id="first-name"
          label="First Name"
          variant="outlined"
          value={firstName}
          onChange={handlers.firstNameChange}
          helperText={firstNameError.message}
          error={firstNameError.hasError}
          disabled={loading}
        />
        <TextField
          id="last-name"
          label="Last Name"
          variant="outlined"
          value={lastName}
          onChange={handlers.lastNameChange}
          helperText={lastNameError.message}
          error={lastNameError.hasError}
          disabled={loading}
        />
        <InternationalPhoneInput
          value={phone}
          onChange={handlers.phoneChange}
          helperText={phoneError.message}
          error={phoneError.hasError}
          disabled={loading}
          isMobile={true}
        />
        <TextField
          id="email-address"
          label="Email Address"
          variant="outlined"
          value={email}
          type="email"
          onChange={handlers.emailChange}
          helperText={emailError.message}
          error={emailError.hasError}
          disabled={loading || isEdit}
        />

        <HeaderDetails>
          <b>User Roles and Permissions</b>
          <TableSubHeader>Set user type and roles below</TableSubHeader>
        </HeaderDetails>

        <TextField
          id="user_type"
          select
          variant="outlined"
          label="User Type"
          value={userType}
          helperText={userTypeError.message}
          error={userTypeError.hasError}
          slotProps={{
            select: {
              native: true,
            },
          }}
          onChange={handlers.userTypeChange}
        >
          {userTypes.map((item) => {
            return (
              <option key={item.value} value={item.value}>
                {item.name}
              </option>
            );
          })}
        </TextField>

        {userType === "CUSTOMER" && (
          <>
            <TextField
              id="customer_organization"
              select
              variant="outlined"
              label="User Organization"
              value={userOrganizationId}
              helperText={userOrganizationIdError.message}
              error={userOrganizationIdError.hasError}
              slotProps={{
                select: {
                  native: true,
                },
              }}
              onChange={handlers.userOrganizationIdChange}
            >
              {organizationsList.map((org) => {
                return (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                );
              })}
            </TextField>
            <TextField
              id="customer_role"
              select
              variant="outlined"
              label="User Role"
              value={userCustomerRole}
              helperText={userCustomerRoleError.message}
              error={userCustomerRoleError.hasError}
              slotProps={{
                select: {
                  native: true,
                },
              }}
              onChange={handlers.userCustomerRoleChange}
            >
              {userTypeCustomerRoles.map((item) => {
                return (
                  <option key={`${item.value}_customer`} value={item.value}>
                    {item.name}
                  </option>
                );
              })}
            </TextField>
          </>
        )}
        {userType === "EMPLOYEE" && (
          <TextField
            id="employee_role"
            select
            variant="outlined"
            label="User Role"
            value={userEmployeeRole}
            helperText={userEmployeeRoleError.message}
            error={userEmployeeRoleError.hasError}
            slotProps={{
              select: {
                native: true,
              },
            }}
            onChange={handlers.userEmployeeRoleChange}
          >
            {userTypeEmployeeRoles.map((item) => {
              return (
                <option key={`${item.value}_employee`} value={item.value}>
                  {item.name}
                </option>
              );
            })}
          </TextField>
        )}
        {userType === "NETWORK" && (
          <>
            {networksList.length ? (
              <div>
                <div>Which networks do you want add this user to?</div>
                <NetworkSelectionContainer>
                  <TextField
                    sx={{ width: "100%" }}
                    id="networkId"
                    select
                    variant="outlined"
                    label="Network"
                    value={networkId}
                    disabled={availableNetworksList.length === 0}
                    helperText={networkIdError.message}
                    error={networkIdError.hasError}
                    slotProps={{
                      select: {
                        native: true,
                      },
                    }}
                    onChange={handlers.networkIdChange}
                  >
                    {availableNetworksList.map((item) => {
                      return (
                        <option key={`${item.id}_network`} value={item.id}>
                          {item.name}
                        </option>
                      );
                    })}
                  </TextField>
                  <Button
                    variant="outlined"
                    // color="error"
                    disabled={availableNetworksList.length === 0}
                    onClick={handlers.addUserToNetworkClick}
                  >
                    Add
                  </Button>
                </NetworkSelectionContainer>
                <div>
                  <HeaderDetails>
                    <b>User Network Organization Access</b>
                    <TableSubHeader>
                      Select the user access level for each organization within
                      the network.
                    </TableSubHeader>
                  </HeaderDetails>
                  {userNetworks.length ? (
                    userNetworks.map((networkItem) => {
                      return (
                        <NetworkOrganization
                          key={`${networkItem.id}_user_network`}
                        >
                          <NetworkOrganizationHeader>
                            <h4>{networkItem.name}</h4>
                            <div>
                              <Button
                                variant="text"
                                size="small"
                                sx={{
                                  color: colors.grayDark,
                                  textTransform: "none",
                                  display: "flex",
                                }}
                                onClick={() => {
                                  handlers.removeUserFromNetworkClick(
                                    networkItem.id
                                  );
                                }}
                              >
                                <TrashIcon sx={{ fontSize: "14px" }} /> Remove
                                User from Network
                              </Button>
                            </div>
                          </NetworkOrganizationHeader>
                          <NetworkOrganizationPermissions>
                            {networkItem.organizations.map((orgItem) => {
                              return (
                                <NetworkOrganizationContainer
                                  key={`${orgItem.id}_${networkItem.id}`}
                                >
                                  <NetworkOrganizationLabel>
                                    {orgItem.name}
                                  </NetworkOrganizationLabel>
                                  <PermissionSwitchContainer>
                                    <FormLabelCustom
                                      className={`${
                                        !userNetworkOrganizationPermissionList.find(
                                          (i) =>
                                            i.networkId === networkItem.id &&
                                            i.organizationId === orgItem.id &&
                                            i.fullAccess
                                        ) && "enabled"
                                      }`}
                                    >
                                      Read Only
                                    </FormLabelCustom>
                                    <FormControlLabel
                                      sx={{ margin: 0 }}
                                      control={
                                        <Switch
                                          checked={
                                            !!userNetworkOrganizationPermissionList.find(
                                              (i) =>
                                                i.networkId ===
                                                  networkItem.id &&
                                                i.organizationId ===
                                                  orgItem.id &&
                                                i.fullAccess
                                            )
                                          }
                                        />
                                      }
                                      onChange={({ target }) => {
                                        const { checked } = target;
                                        handlers.networkOrganizationPermissionChange(
                                          {
                                            _networkId: networkItem.id,
                                            _organizationId: orgItem.id,
                                            _fullAccess: checked,
                                          }
                                        );
                                      }}
                                    />
                                    <FormLabelCustom
                                      className={`${
                                        !!userNetworkOrganizationPermissionList.find(
                                          (i) =>
                                            i.networkId === networkItem.id &&
                                            i.organizationId === orgItem.id &&
                                            i.fullAccess
                                        ) && "enabled"
                                      }`}
                                    >
                                      Full Access
                                    </FormLabelCustom>
                                  </PermissionSwitchContainer>
                                </NetworkOrganizationContainer>
                              );
                            })}
                          </NetworkOrganizationPermissions>
                        </NetworkOrganization>
                      );
                    })
                  ) : (
                    <>
                      {userNetworksError.hasError && (
                        <NetworksErrorContainer>
                          <FormSubmitError>
                            <ErrorIcon fontSize="small" />
                            {userNetworksError.message}
                          </FormSubmitError>
                        </NetworksErrorContainer>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <NoChartDataPlaceholder
                header={"Network User Types Require a Network"}
                body={
                  "There are no Networks in the system yet. Please create a network for it to show up here"
                }
                buttonText={"create network"}
                buttonPath={"/admin/networks"}
              />
            )}
          </>
        )}

        {showDeactivate && (
          <>
            {isEdit && user.deleted && (
              <Button
                variant="outlined"
                color="error"
                onClick={handlers.reactivateUser}
                disabled={loading}
              >
                REACTIVATE USER
              </Button>
            )}
            {isEdit && !user.deleted && (
              <Button
                variant="outlined"
                color="error"
                onClick={handlers.deactivateUser}
                disabled={loading}
              >
                DEACTIVATE USER
              </Button>
            )}
          </>
        )}

        {isEdit && user?.invitePending && (
          <Button
            variant="outlined"
            // color="error"
            onClick={handlers.resendUserInvite}
            disabled={loading}
          >
            RESEND INVITE EMAIL
          </Button>
        )}

        {error && (
          <FormSubmitError>
            <ErrorIcon fontSize="small" />
            {error}
          </FormSubmitError>
        )}

        <ActionButtonsContainer>
          <Button onClick={handlers.cancel}>Cancel</Button>
          {loading ? (
            <Button disabled variant="contained">
              Saving&#8230;
            </Button>
          ) : (
            <Button onClick={handlers.save} variant="contained">
              Save
            </Button>
          )}
        </ActionButtonsContainer>
      </InputFieldsWrapper>
    </FormContainer>
  );
};

export default AdminUserForm;
